
import React, { useEffect, useState } from "react";
import { ExtendedOrganization } from "@/hooks/organization-settings/types";
import { useToast } from "@/components/ui/use-toast";
import { useFetchSubscriptionPlans } from "@/hooks/useFetchSubscriptionPlans";
import { LoadingPlans } from "./subscription/LoadingPlans";
import { PlansList } from "./subscription/PlansList";
import { fallbackPlans } from "./subscription/FallbackPlans";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";

interface SubscriptionPlansProps {
  organization: ExtendedOrganization | null;
  onChangePlan: () => void;
}

export const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({ 
  organization,
  onChangePlan
}) => {
  const { toast } = useToast();
  const { plans, isLoading, error, isError, refetch } = useFetchSubscriptionPlans();
  const [isRetrying, setIsRetrying] = useState(false);
  
  // Tentative de rechargement automatique en cas d'erreur
  useEffect(() => {
    if (isError) {
      console.error("Error loading subscription plans:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les plans d'abonnement. Tentative de rechargement...",
        variant: "destructive",
      });
      
      setIsRetrying(true);
      
      // Essayer de recharger les données après un court délai
      const timer = setTimeout(() => {
        refetch().finally(() => {
          setIsRetrying(false);
        });
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isError, error, toast, refetch]);

  console.log("SubscriptionPlans - Organization:", organization);
  console.log("SubscriptionPlans - Plans:", plans);
  console.log("SubscriptionPlans - Loading:", isLoading);
  console.log("SubscriptionPlans - Error:", error);
  console.log("SubscriptionPlans - IsRetrying:", isRetrying);

  // Afficher le skeleton de chargement pendant le chargement initial ou pendant une nouvelle tentative
  if (isLoading || isRetrying) {
    console.log("Displaying loading skeleton - Loading in progress or retrying");
    return <LoadingPlans />;
  }

  // Si aucun plan n'est récupéré, utiliser des données de secours
  const displayPlans = plans.length > 0 ? plans : fallbackPlans;
  
  return (
    <Card className="border-0 shadow-md dark:border dark:border-gray-800 overflow-hidden rounded-xl">
      <CardHeader className="bg-white dark:bg-[#121624] border-b border-gray-100 dark:border-[rgba(60,63,84,0.6)] p-6">
        <CardTitle className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Plans d'abonnement</CardTitle>
        <CardDescription className="text-gray-500 dark:text-gray-400">
          Choisissez le plan qui correspond le mieux à vos besoins
        </CardDescription>
      </CardHeader>
      
      <CardContent className="p-6 bg-white dark:bg-[#121624] space-y-6">
        {isError && (
          <div className="mb-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>Impossible de charger les plans d'abonnement</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    setIsRetrying(true);
                    refetch().finally(() => setIsRetrying(false));
                  }}
                  className="ml-4"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Réessayer
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        )}
        
        <PlansList 
          plans={displayPlans} 
          organization={organization} 
          onChangePlan={onChangePlan} 
          error={null} 
        />
      </CardContent>
    </Card>
  );
};
